import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";

import { useParams, useNavigate } from "react-router-dom";

import FollowButton from "../components/FollowButton";
import LeftMenu from "../components/LeftMenu";
import TeamDetailAboutTab from "../components/team-detail/TeamDetailAboutTab";
import TeamDetailFixturesTab from "../components/team-detail/TeamDetailFixturesTab";
import TeamDetailHero from "../components/team-detail/TeamDetailHero";
import TeamDetailSquadTab from "../components/team-detail/TeamDetailSquadTab";
import TeamDetailStandingsTab from "../components/team-detail/TeamDetailStandingsTab";
import { getTeamByName } from "../data/teamsDatabase";
import apiService from "../services/api";
import { sportsDbFetch } from "../utils/sportsDbCache";
import {
  isTeamFollowed,
  getTeamFollowEntry,
  setTeamNotificationPref,
  getFollowedPlayersByTeam,
} from "../utils/sportsPreferences";
import webPlayerStorage from "../utils/webPlayerStorage";

// ─── Constants ──────────────────────────────────────────────────────
const APISPORTS_KEY = "50b71acfe2fa82c70a5a5014b5dea076";

const EVENT_BASED_LEAGUES = [
  "ufc",
  "bellator",
  "formula 1",
  "f1",
  "nascar",
  "indycar",
  "motogp",
  "boxing",
  "mma",
  "formula1",
  "pga",
  "wwe",
];

const TABS = [
  { id: "squad", label: "Squad", icon: "fas fa-users" },
  { id: "fixtures", label: "Fixtures", icon: "fas fa-calendar-alt" },
  { id: "standings", label: "Standings", icon: "fas fa-trophy" },
  { id: "about", label: "About", icon: "fas fa-info-circle" },
];

// ─── Sport helpers ──────────────────────────────────────────────────
const getSportType = (sport) => {
  if (!sport) return "other";
  const s = sport.toLowerCase();

  if (s.includes("soccer") || s === "football") return "soccer";
  if (
    s.includes("american football") ||
    s.includes("nfl") ||
    s.includes("ncaaf")
  )
    return "nfl";
  if (s.includes("basketball") || s.includes("nba")) return "nba";
  if (s.includes("ice hockey") || s.includes("nhl")) return "nhl";
  if (s.includes("baseball") || s.includes("mlb")) return "mlb";

  return "other";
};

const getAPISportsBaseUrl = (sportType) => {
  const map = {
    soccer: "https://v3.football.api-sports.io",
    nfl: "https://v1.american-football.api-sports.io",
    nba: "https://v1.basketball.api-sports.io",
    nhl: "https://v1.hockey.api-sports.io",
    mlb: "https://v1.baseball.api-sports.io",
  };

  return map[sportType] || null;
};

const apiSportsFetch = async (url) => {
  const res = await fetch(url, {
    headers: { "x-apisports-key": APISPORTS_KEY },
  });

  if (!res.ok) throw new Error(`API-Sports ${res.status}`);

  return res.json();
};

const guessLeagueId = (name, sportType) => {
  const localTeam = getTeamByName(name);

  if (localTeam) {
    const leagueMap = {
      "Premier League": 39,
      "La Liga": 140,
      Bundesliga: 78,
      "Serie A": 135,
      "Ligue 1": 61,
      MLS: 253,
      NFL: "nfl",
      NBA: "nba",
      NHL: "nhl",
      MLB: "mlb",
    };

    return leagueMap[localTeam.league] || null;
  }
  if (sportType !== "soccer") return sportType;

  return null;
};

const mapPlayer = (p) => ({
  id: p.idPlayer,
  name: p.strPlayer,
  age: p.dateBorn
    ? new Date().getFullYear() - new Date(p.dateBorn).getFullYear()
    : null,
  number: p.strNumber || null,
  position: p.strPosition || "Unknown",
  photo: p.strCutout || p.strThumb || null,
  nationality: p.strNationality || null,
  height: p.strHeight || null,
  bio: p.strDescriptionEN || null,
  thumbUrl: p.strCutout || p.strThumb || null,
  signing: p.strSigning || null,
});

const STAFF_KEYWORDS = [
  "Manager",
  "Coach",
  "Owner",
  "Director",
  "Fitness",
  "Assistant",
];
const isStaffPosition = (pos) => STAFF_KEYWORDS.some((k) => pos?.includes(k));

// ─── Component ──────────────────────────────────────────────────────
const WebPlayerTeamDetail = () => {
  const { teamName } = useParams();
  const navigate = useNavigate();
  const decodedTeamName = decodeURIComponent(teamName).trim();

  // ─── Phase 1 hero state ─────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [sportsDbTeam, setSportsDbTeam] = useState(null);
  const [nextMatch, setNextMatch] = useState(null);
  const [liveMatch, setLiveMatch] = useState(null);
  const [nextMatchLoading, setNextMatchLoading] = useState(true);
  const [recentEvents, setRecentEvents] = useState([]);
  const [coach, setCoach] = useState(null);
  const [activeTab, setActiveTab] = useState("squad");
  const [showNotifToggles, setShowNotifToggles] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [followVersion, setFollowVersion] = useState(0); // eslint-disable-line no-unused-vars -- triggers re-render for follow state

  // ─── Per-tab state ──────────────────────────────────────────────
  const [squadData, setSquadData] = useState({
    sportsDbPlayers: null,
    positionGroups: {},
    loading: true,
  });
  const [fixturesData, setFixturesData] = useState({
    upcoming: [],
    recent: [],
    loading: true,
  });
  const [standingsData, setStandingsData] = useState({
    standings: null,
    loading: true,
  });
  const [aboutData, setAboutData] = useState({
    honours: [],
    equipment: [],
    loading: true,
  });

  // ─── Tab cache ref ──────────────────────────────────────────────
  const tabCacheRef = useRef({});

  // ─── Refs for DPAD ──────────────────────────────────────────────
  const heroRef = useRef(null);
  const tabBarRef = useRef(null);
  const tabContentRef = useRef(null);
  const teamRowRef = useRef(null);

  const playlist = webPlayerStorage.getPlaylist();
  const profile = webPlayerStorage.getProfile();

  // ─── Event-based league redirect ────────────────────────────────
  useEffect(() => {
    const lower = decodedTeamName.toLowerCase();

    if (EVENT_BASED_LEAGUES.includes(lower)) {
      navigate(
        `/web-player/sports/league-events/${encodeURIComponent(teamName)}`,
        { replace: true }
      );
    }
  }, [decodedTeamName, navigate, teamName]);

  // ─── Phase 1: Hero data fetch ──────────────────────────────────
  const fetchHeroData = useCallback(async (name) => {
    const data = await sportsDbFetch(
      `searchteams.php?t=${encodeURIComponent(name)}`
    );
    const teams = data?.teams;

    if (!teams || teams.length === 0) return null;

    const team =
      teams.find((t) => t.strTeam.toLowerCase() === name.toLowerCase()) ||
      teams[0];

    setSportsDbTeam(team);

    if (team.idTeam) {
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const [nextData, lastData, liveData] = await Promise.allSettled([
        sportsDbFetch(`eventsnext.php?id=${team.idTeam}`).catch(() => null),
        sportsDbFetch(`eventslast.php?id=${team.idTeam}`).catch(() => null),
        apiService
          .webPlayerRequest(
            `/v2/sports/home?timestamp=${timestamp}&timezone=${encodeURIComponent(timezone)}`
          )
          .catch(() => null),
      ]);

      // Check for live match involving this team
      const homeData = liveData.value?.data || liveData.value || {};
      const carousel = homeData.carousel || [];
      const matchesTeam = (n) => {
        if (!n) return false;
        const a = n.toLowerCase();
        const b = name.toLowerCase();

        return a === b || a.includes(b) || b.includes(a);
      };
      const liveGame = carousel.find((evt) => {
        const isLive = evt.status
          ? evt.status.short !== "NS" &&
            evt.status.short !== "TBD" &&
            evt.status.short !== "PST" &&
            evt.status.short !== "FT" &&
            evt.status.short !== "AOT" &&
            evt.status.short !== "AET"
          : false;

        if (!isLive) return false;
        const home = evt.teams?.home?.name || evt.strHomeTeam || "";
        const away = evt.teams?.away?.name || evt.strAwayTeam || "";

        return matchesTeam(home) || matchesTeam(away);
      });

      if (liveGame) {
        setLiveMatch({
          home: liveGame.teams?.home?.name || liveGame.strHomeTeam,
          away: liveGame.teams?.away?.name || liveGame.strAwayTeam,
          homeLogo:
            liveGame.teams?.home?.logo || liveGame.strHomeTeamBadge || null,
          awayLogo:
            liveGame.teams?.away?.logo || liveGame.strAwayTeamBadge || null,
          homeScore:
            liveGame.goals?.home ?? liveGame.scores?.home?.total ?? null,
          awayScore:
            liveGame.goals?.away ?? liveGame.scores?.away?.total ?? null,
          status: liveGame.status?.short || "LIVE",
          elapsed: liveGame.status?.elapsed || null,
          date: liveGame.date || liveGame.fixture?.date || null,
          venue: liveGame.fixture?.venue?.name || liveGame.strVenue || null,
          raw: liveGame,
        });
      }

      const nextEvents = nextData.value?.events || [];

      if (nextEvents.length > 0) {
        const evt = nextEvents[0];

        setNextMatch({
          home: evt.strHomeTeam,
          away: evt.strAwayTeam,
          homeLogo: evt.strHomeTeamBadge || null,
          awayLogo: evt.strAwayTeamBadge || null,
          date: evt.dateEvent + (evt.strTime ? `T${evt.strTime}` : ""),
          venue: evt.strVenue,
          raw: evt,
        });
      }
      setNextMatchLoading(false);

      const lastEvents = lastData.value?.results || [];

      setRecentEvents(lastEvents);
    } else {
      setNextMatchLoading(false);
    }

    return team;
  }, []);

  // ─── Tab loaders ────────────────────────────────────────────────
  const loadSquadTab = useCallback(
    async (team) => {
      if (tabCacheRef.current.squad) {
        setSquadData(tabCacheRef.current.squad);

        return;
      }
      setSquadData((prev) => ({ ...prev, loading: true }));

      let sdbPlayers = null;

      if (team?.idTeam) {
        const d = await sportsDbFetch(
          `lookup_all_players.php?id=${team.idTeam}`
        ).catch(() => null);

        sdbPlayers = d?.player || null;
      }

      // Extract coach
      if (sdbPlayers) {
        const mgr = sdbPlayers.find(
          (p) =>
            p.strPosition === "Manager" || p.strPosition?.includes("Head Coach")
        );

        if (mgr) setCoach(mgr.strPlayer);
      }

      // Build position groups (excluding staff)
      const playerList = (sdbPlayers || []).filter(
        (p) => !isStaffPosition(p.strPosition)
      );
      const groups = {
        Goalkeeper: [],
        Defender: [],
        Midfielder: [],
        Attacker: [],
        Other: [],
      };

      playerList.forEach((p) => {
        const pos = p.strPosition || "Other";

        if (pos.includes("Goal") || pos.includes("Keeper"))
          groups.Goalkeeper.push(mapPlayer(p));
        else if (pos.includes("Defend") || pos.includes("Back"))
          groups.Defender.push(mapPlayer(p));
        else if (pos.includes("Mid")) groups.Midfielder.push(mapPlayer(p));
        else if (
          pos.includes("Att") ||
          pos.includes("Forward") ||
          pos.includes("Strik")
        )
          groups.Attacker.push(mapPlayer(p));
        else groups.Other.push(mapPlayer(p));
      });
      const positionGroups = Object.fromEntries(
        Object.entries(groups).filter(([, v]) => v.length > 0)
      );

      // Try API-Sports enrichment for soccer squads
      const sportType = getSportType(team?.strSport || "Soccer");
      const baseUrl = getAPISportsBaseUrl(sportType);

      if (baseUrl && sportType === "soccer") {
        try {
          const teamData = await apiSportsFetch(
            `${baseUrl}/teams?search=${encodeURIComponent(team.strTeam)}`
          );
          const apiTeam =
            teamData.response?.find(
              (t) =>
                !t.team.name.includes("U21") &&
                !t.team.name.includes("U19") &&
                !t.team.name.includes("U18") &&
                !t.team.name.includes(" W")
            ) || teamData.response?.[0];

          if (apiTeam?.team?.id) {
            const sqData = await apiSportsFetch(
              `${baseUrl}/players/squads?team=${apiTeam.team.id}`
            ).catch(() => null);
            const apiPlayers = sqData?.response?.[0]?.players;

            if (apiPlayers && sdbPlayers) {
              // Merge API-Sports data into position groups
              apiPlayers.forEach((ap) => {
                const lastName = ap.name?.split(" ").pop()?.toLowerCase();
                const sdbMatch = sdbPlayers.find((p) =>
                  p.strPlayer?.toLowerCase().includes(lastName || "___nomatch")
                );
                const merged = {
                  id: sdbMatch?.idPlayer || ap.id,
                  apiSportsId: ap.id,
                  name: ap.name,
                  age: ap.age,
                  number: ap.number,
                  position: ap.position || "Unknown",
                  photo: ap.photo,
                  nationality: sdbMatch?.strNationality || null,
                  height: sdbMatch?.strHeight || null,
                  bio: sdbMatch?.strDescriptionEN || null,
                  thumbUrl:
                    sdbMatch?.strCutout || sdbMatch?.strThumb || ap.photo,
                  signing: sdbMatch?.strSigning || null,
                };
                const pos = merged.position || "Other";

                if (pos.includes("Goal") || pos.includes("Keeper")) {
                  if (!positionGroups.Goalkeeper)
                    positionGroups.Goalkeeper = [];
                  positionGroups.Goalkeeper.push(merged);
                } else if (pos.includes("Defend") || pos.includes("Back")) {
                  if (!positionGroups.Defender) positionGroups.Defender = [];
                  positionGroups.Defender.push(merged);
                } else if (pos.includes("Mid")) {
                  if (!positionGroups.Midfielder)
                    positionGroups.Midfielder = [];
                  positionGroups.Midfielder.push(merged);
                } else if (
                  pos.includes("Att") ||
                  pos.includes("Forward") ||
                  pos.includes("Strik")
                ) {
                  if (!positionGroups.Attacker) positionGroups.Attacker = [];
                  positionGroups.Attacker.push(merged);
                }
              });
              // Deduplicate by name within each group
              Object.keys(positionGroups).forEach((key) => {
                const seen = new Set();

                positionGroups[key] = positionGroups[key].filter((p) => {
                  const k = p.name?.toLowerCase();

                  if (seen.has(k)) return false;
                  seen.add(k);

                  return true;
                });
              });
            }
          }
        } catch {
          /* API-Sports enrichment is optional */
        }
      }

      const result = {
        sportsDbPlayers: sdbPlayers,
        positionGroups,
        loading: false,
      };

      tabCacheRef.current.squad = result;
      setSquadData(result);
    },
    [setCoach]
  );

  const loadFixturesTab = useCallback(
    async (team) => {
      if (tabCacheRef.current.fixtures) {
        setFixturesData(tabCacheRef.current.fixtures);

        return;
      }
      setFixturesData((prev) => ({ ...prev, loading: true }));

      const sportType = getSportType(team?.strSport || "Soccer");
      const baseUrl = getAPISportsBaseUrl(sportType);
      let upcoming = [];
      let recent = [];

      if (baseUrl && sportType === "soccer") {
        // Try API-Sports for soccer fixtures
        try {
          const teamData = await apiSportsFetch(
            `${baseUrl}/teams?search=${encodeURIComponent(team.strTeam)}`
          );
          const apiTeam =
            teamData.response?.find(
              (t) =>
                !t.team.name.includes("U21") &&
                !t.team.name.includes("U19") &&
                !t.team.name.includes(" W")
            ) || teamData.response?.[0];
          const teamId = apiTeam?.team?.id;

          if (teamId) {
            const [upRes, recRes] = await Promise.allSettled([
              apiSportsFetch(`${baseUrl}/fixtures?team=${teamId}&next=10`)
                .then((d) => d.response || [])
                .catch(() => []),
              apiSportsFetch(`${baseUrl}/fixtures?team=${teamId}&last=10`)
                .then((d) => d.response || [])
                .catch(() => []),
            ]);

            upcoming = upRes.value || [];
            recent = recRes.value || [];
          }
        } catch {
          /* fall through to backend */
        }
      }

      // Fallback to backend API for non-soccer or if API-Sports failed
      if (upcoming.length === 0 && recent.length === 0) {
        try {
          const sportKey = sportType;
          const today = new Date();
          const userTimestamp = today.toISOString();
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const fetchDays = async (offset, count) => {
            const promises = [];

            for (let i = 0; i < count; i++) {
              const d = new Date(today);

              d.setDate(d.getDate() + offset + i);
              const ds = d.toISOString().split("T")[0];
              const endpoint = `/v2/sports/fixtures/${sportKey}?date=${ds}&timestamp=${userTimestamp}&timezone=${encodeURIComponent(userTimezone)}`;

              promises.push(
                apiService
                  .webPlayerRequest(endpoint)
                  .then((r) => r?.data?.fixtures || [])
                  .catch(() => [])
              );
            }

            return (await Promise.all(promises)).flat();
          };

          const matchesTeam = (n) => {
            if (!n) return false;
            const a = n.toLowerCase();
            const b = decodedTeamName.toLowerCase();

            return a === b || a.includes(b) || b.includes(a);
          };
          const filterTeam = (games) =>
            games.filter((g) => {
              const home = g.teams?.home?.name || g.strHomeTeam || g.homeTeam;
              const away = g.teams?.away?.name || g.strAwayTeam || g.awayTeam;

              return matchesTeam(home) || matchesTeam(away);
            });

          const [upAll, pastAll] = await Promise.all([
            fetchDays(0, 14),
            fetchDays(-14, 14),
          ]);

          upcoming = filterTeam(upAll);
          recent = filterTeam(pastAll);
        } catch (err) {
          console.error("[Backend] Fixtures fetch failed:", err);
        }
      }

      // Update nextMatch if hero didn't get one
      if (!nextMatch && upcoming.length > 0) {
        const f = upcoming[0];

        setNextMatch({
          home: f.teams?.home?.name || f.strHomeTeam || f.homeTeam,
          away: f.teams?.away?.name || f.strAwayTeam || f.awayTeam,
          homeLogo: f.teams?.home?.logo || f.homeTeamLogo || f.strHomeTeamBadge,
          awayLogo: f.teams?.away?.logo || f.awayTeamLogo || f.strAwayTeamBadge,
          date: f.fixture?.date || f.date || f.dateEvent,
          venue: f.fixture?.venue?.name || f.venue?.name || f.strVenue,
          raw: f,
        });
        setNextMatchLoading(false);
      }

      const result = { upcoming, recent, loading: false };

      tabCacheRef.current.fixtures = result;
      setFixturesData(result);
    },
    [decodedTeamName, nextMatch]
  );

  const loadStandingsTab = useCallback(
    async (team) => {
      if (tabCacheRef.current.standings) {
        setStandingsData(tabCacheRef.current.standings);

        return;
      }
      setStandingsData((prev) => ({ ...prev, loading: true }));

      const sportType = getSportType(team?.strSport || "Soccer");
      const leagueId = guessLeagueId(decodedTeamName, sportType);
      let standings = null;

      if (leagueId) {
        try {
          if (sportType === "soccer") {
            const res = await apiService.webPlayerRequest(
              `/v2/sports/standings/${leagueId}`
            );
            const data = res?.data?.standings;

            if (data) standings = Array.isArray(data[0]) ? data : [data];
          } else {
            const res = await apiService.webPlayerRequest(
              `/v2/sports/standings/${sportType}`
            );
            const data = res?.data?.standings || [];

            if (data.length > 0 && Array.isArray(data[0])) {
              standings = data;
            } else {
              standings = [data];
            }
          }
        } catch (err) {
          console.error("[Standings] Fetch failed:", err);
        }
      }

      const result = { standings, loading: false };

      tabCacheRef.current.standings = result;
      setStandingsData(result);
    },
    [decodedTeamName]
  );

  const loadAboutTab = useCallback(async (team) => {
    if (tabCacheRef.current.about) {
      setAboutData(tabCacheRef.current.about);

      return;
    }
    setAboutData((prev) => ({ ...prev, loading: true }));

    let honours = [];
    let equipment = [];

    if (team?.idTeam) {
      const [honoursData, equipData] = await Promise.allSettled([
        sportsDbFetch(`lookuphonours.php?id=${team.idTeam}`).catch(() => null),
        sportsDbFetch(`lookupequipment.php?id=${team.idTeam}`).catch(
          () => null
        ),
      ]);

      honours = honoursData.value?.honours || [];
      equipment = equipData.value?.equipment || [];
    }

    const result = { honours, equipment, loading: false };

    tabCacheRef.current.about = result;
    setAboutData(result);
  }, []);

  // ─── Main mount effect ──────────────────────────────────────────
  useEffect(() => {
    const lower = decodedTeamName.toLowerCase();

    if (EVENT_BASED_LEAGUES.includes(lower)) return;

    let cancelled = false;

    const load = async () => {
      // Reset all state
      setLoading(true);
      setSportsDbTeam(null);
      setNextMatch(null);
      setNextMatchLoading(true);
      setRecentEvents([]);
      setCoach(null);
      setActiveTab("squad");
      setShowNotifToggles(false);
      setAboutExpanded(false);
      setSquadData({
        sportsDbPlayers: null,
        positionGroups: {},
        loading: true,
      });
      setFixturesData({ upcoming: [], recent: [], loading: true });
      setStandingsData({ standings: null, loading: true });
      setAboutData({ honours: [], equipment: [], loading: true });
      tabCacheRef.current = {};

      try {
        await fetchHeroData(decodedTeamName);
        if (cancelled) return;
        setLoading(false);
      } catch (err) {
        console.error("[TeamDetail] Hero fetch failed:", err);
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [decodedTeamName, fetchHeroData]);

  // ─── Tab change effect ──────────────────────────────────────────
  useEffect(() => {
    if (!sportsDbTeam) return;
    const loaders = {
      squad: () => loadSquadTab(sportsDbTeam),
      fixtures: () => loadFixturesTab(sportsDbTeam),
      standings: () => loadStandingsTab(sportsDbTeam),
      about: () => loadAboutTab(sportsDbTeam),
    };

    loaders[activeTab]?.();
  }, [
    activeTab,
    sportsDbTeam,
    loadSquadTab,
    loadFixturesTab,
    loadStandingsTab,
    loadAboutTab,
  ]);

  // ─── Derived state ─────────────────────────────────────────────
  const teamColors = useMemo(() => {
    if (!sportsDbTeam) return { primary: "#3B82F6", secondary: "#8B5CF6" };

    return {
      primary: sportsDbTeam.strColour1 || "#3B82F6",
      secondary: sportsDbTeam.strColour2 || "#8B5CF6",
      tertiary: sportsDbTeam.strColour3 || null,
    };
  }, [sportsDbTeam]);

  const badge = useMemo(() => {
    if (sportsDbTeam?.strBadge) return sportsDbTeam.strBadge;
    const local = getTeamByName(decodedTeamName);

    return local?.logo || null;
  }, [sportsDbTeam, decodedTeamName]);

  const leagueName = useMemo(() => {
    if (sportsDbTeam?.strLeague) return sportsDbTeam.strLeague;
    const local = getTeamByName(decodedTeamName);

    return local?.league || null;
  }, [sportsDbTeam, decodedTeamName]);

  const record = useMemo(() => {
    if (standingsData.standings) {
      const flat = standingsData.standings.flat();
      const row = flat.find((t) => {
        const n = t.team?.name || t.name || "";

        return (
          n.toLowerCase() === decodedTeamName.toLowerCase() ||
          n.toLowerCase().includes(decodedTeamName.toLowerCase()) ||
          decodedTeamName.toLowerCase().includes(n.toLowerCase())
        );
      });

      if (row) {
        return {
          wins: row.all?.win ?? row.games?.win?.total ?? row.wins ?? 0,
          draws: row.all?.draw ?? row.games?.draw?.total ?? row.ties ?? 0,
          losses: row.all?.lose ?? row.games?.lose?.total ?? row.losses ?? 0,
          points: row.points ?? null,
          position: row.rank ?? row.position ?? null,
        };
      }
    }

    return null;
  }, [standingsData.standings, decodedTeamName]);

  // ─── Follow state ───────────────────────────────────────────────
  const followStatus = isTeamFollowed(decodedTeamName);
  const followEntry = getTeamFollowEntry(decodedTeamName);
  const notifPrefs = followEntry?.notifications || {};
  const followedPlayers = getFollowedPlayersByTeam(decodedTeamName);

  const handleFollowChange = useCallback(() => {
    setFollowVersion((v) => v + 1);
  }, []);

  // ─── Navigation helpers ─────────────────────────────────────────
  const handleGameClick = useCallback(
    (game) => {
      const eventId =
        game.fixture?.id || game.id || game.mediaId || game.idEvent;

      navigate(`/web-player/sports/event/${eventId}`, {
        state: { event: game },
      });
    },
    [navigate]
  );

  const handlePlayerClick = useCallback(
    (player) => {
      navigate(`/web-player/sports/player/${player.id}`, {
        state: { player },
      });
    },
    [navigate]
  );

  const handleMatchClick = useCallback(
    (match) => {
      if (match?.raw) handleGameClick(match.raw);
    },
    [handleGameClick]
  );

  const handleNotifToggle = useCallback(
    (key, isOn) => {
      setTeamNotificationPref(decodedTeamName, key, !isOn);
      handleFollowChange();
    },
    [decodedTeamName, handleFollowChange]
  );

  // ─── DPAD Focus Management ─────────────────────────────────────
  const handleKeyDown = useCallback(
    (e) => {
      const { key } = e;
      const active = document.activeElement;

      if (key === "Escape" || key === "Backspace") {
        e.preventDefault();
        navigate(-1);

        return;
      }

      // Tab bar navigation
      if (tabBarRef.current?.contains(active)) {
        if (key === "ArrowLeft" || key === "ArrowRight") {
          e.preventDefault();
          const tabIds = TABS.map((t) => t.id);
          const idx = tabIds.indexOf(activeTab);
          const next =
            key === "ArrowRight"
              ? tabIds[(idx + 1) % tabIds.length]
              : tabIds[(idx - 1 + tabIds.length) % tabIds.length];

          setActiveTab(next);
          setTimeout(() => {
            tabBarRef.current?.querySelector(`[data-tab="${next}"]`)?.focus();
          }, 50);
        }
        if (key === "ArrowDown") {
          e.preventDefault();
          const first = tabContentRef.current?.querySelector(
            "[tabindex='0'], button, a"
          );

          first?.focus();
        }
        if (key === "ArrowUp") {
          e.preventDefault();
          heroRef.current?.querySelector("button")?.focus();
        }
      }

      // Hero navigation
      if (heroRef.current?.contains(active)) {
        if (key === "ArrowDown") {
          e.preventDefault();
          tabBarRef.current
            ?.querySelector(`[data-tab="${activeTab}"]`)
            ?.focus();
        }
      }

      // Tab content navigation
      if (tabContentRef.current?.contains(active)) {
        if (key === "ArrowUp" && !e.target.closest("[data-carousel]")) {
          e.preventDefault();
          tabBarRef.current
            ?.querySelector(`[data-tab="${activeTab}"]`)
            ?.focus();
        }
      }
    },
    [activeTab, navigate]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen">
      <LeftMenu playlist={playlist} profile={profile} />

      <TeamDetailHero
        badge={badge}
        coach={coach}
        decodedTeamName={decodedTeamName}
        formResults={recentEvents}
        heroRef={heroRef}
        leagueName={leagueName}
        liveMatch={liveMatch}
        loading={loading}
        nextMatch={nextMatch}
        nextMatchLoading={nextMatchLoading}
        record={record}
        sportsDbTeam={sportsDbTeam}
        teamColors={teamColors}
        onBack={() => navigate(-1)}
        onMatchClick={handleMatchClick}
      />

      {/* Hero bottom gradient — dark to transparent fade below hero */}
      <div
        className="pointer-events-none relative z-[5]"
        style={{
          height: "16rem",
          marginTop: "0rem",
          marginBottom: "-16rem",
          background:
            "linear-gradient(to bottom, rgba(10,14,26,1) 0%, rgba(10,14,26,0.9) 25%, rgba(10,14,26,0.7) 40%, rgba(10,14,26,0.4) 60%, rgba(10,14,26,0.15) 80%, transparent 100%)",
        }}
      />

      <div className="pl-20" style={{ position: "relative", zIndex: 10 }}>
        {/* ─── TAB BAR ─────────────────────────────────────────── */}
        <div
          className="sticky top-0 z-30 backdrop-blur-xl px-8 py-3"
          ref={tabBarRef}
        >
          <div className="flex items-center gap-2">
            {/* Follow button — first in the row */}
            <FollowButton
              showTier
              entity={decodedTeamName}
              size="md"
              type="team"
              variant="full"
              onFollowChange={handleFollowChange}
            />
            {followStatus.followed && (
              <button
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-white/30 ${
                  showNotifToggles
                    ? "bg-blue-500/20 text-blue-400 border border-blue-500/40"
                    : "bg-white/10 text-white/50 border border-white/10 hover:bg-white/15"
                }`}
                tabIndex={0}
                title="Notification settings"
                onClick={() => setShowNotifToggles((v) => !v)}
              >
                <i className="fas fa-bell text-xs" />
              </button>
            )}

            <div className="w-px h-6 bg-white/[0.08] mx-1" />

            {TABS.filter(
              (tab) =>
                tab.id !== "standings" ||
                (standingsData.standings && standingsData.standings.length > 0)
            ).map((tab) => (
              <button
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-white/30 ${
                  activeTab === tab.id
                    ? "text-white shadow-lg"
                    : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70"
                }`}
                data-tab={tab.id}
                key={tab.id}
                style={
                  activeTab === tab.id
                    ? {
                        background: `${teamColors.primary}40`,
                        borderColor: `${teamColors.primary}60`,
                      }
                    : undefined
                }
                tabIndex={0}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Notification toggles (expands below tab bar) */}
          {showNotifToggles && followStatus.followed && (
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                {
                  key: "matchStart",
                  label: "Match Start",
                  icon: "fa-play-circle",
                },
                {
                  key: "scoreChange",
                  label: "Score Change",
                  icon: "fa-futbol",
                },
                {
                  key: "finalScore",
                  label: "Final Score",
                  icon: "fa-flag-checkered",
                },
              ].map((item) => {
                const isOn = notifPrefs[item.key] !== false;

                return (
                  <button
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isOn
                        ? "bg-blue-500/15 border border-blue-500/30 text-blue-300"
                        : "bg-white/5 border border-white/[0.08] text-white/30"
                    }`}
                    key={item.key}
                    onClick={() => handleNotifToggle(item.key, !isOn)}
                  >
                    <i className={`fas ${item.icon}`} />
                    {item.label}
                    <i
                      className={`fas ${isOn ? "fa-toggle-on text-blue-400" : "fa-toggle-off text-white/20"}`}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ─── TAB CONTENT ─────────────────────────────────────── */}
        <div className="px-8 py-8 max-w-7xl" ref={tabContentRef}>
          {/* Followed players rail */}
          {followedPlayers.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-3">
                Players You Follow
              </h3>
              <div
                className="flex gap-3 overflow-x-auto pb-2"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(255,255,255,0.1) transparent",
                }}
              >
                {followedPlayers.map((p) => (
                  <button
                    className="flex-shrink-0 flex items-center gap-3 bg-white/5 rounded-xl border border-white/[0.08] p-3 hover:border-blue-500/30 transition-all focus:outline-none focus:ring-2 focus:ring-blue-400/50"
                    key={p.key}
                    tabIndex={0}
                    onClick={() =>
                      navigate(`/web-player/sports/player/${p.metadata?.id}`)
                    }
                  >
                    {p.metadata?.photo ? (
                      <img
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                        src={p.metadata.photo}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                        <i className="fas fa-user text-blue-400 text-xs" />
                      </div>
                    )}
                    <div className="text-left">
                      <p className="text-white text-sm font-semibold">
                        {p.metadata?.name}
                      </p>
                      <p className="text-white/40 text-xs">
                        {p.metadata?.position}
                      </p>
                    </div>
                    <i className="fas fa-chevron-right text-white/20 text-xs ml-2" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === "squad" && (
            <>
              {/* Recent Results — above staff/squad */}
              {recentEvents && recentEvents.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-3">
                    Recent Results
                  </h3>
                  <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden divide-y divide-white/[0.04]">
                    {recentEvents.slice(0, 5).map((evt) => {
                      const homeWin =
                        Number(evt.intHomeScore) > Number(evt.intAwayScore);
                      const draw = evt.intHomeScore === evt.intAwayScore;
                      const isHome = evt.idHomeTeam === sportsDbTeam?.idTeam;
                      const won = isHome ? homeWin : !homeWin && !draw;

                      return (
                        <div
                          className="flex items-center gap-4 p-3 px-5"
                          key={evt.idEvent}
                        >
                          <div className="w-20 text-xs text-white/30 flex-shrink-0">
                            {evt.dateEvent}
                          </div>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {evt.strHomeTeamBadge && (
                              <img
                                alt=""
                                className="w-5 h-5 object-contain flex-shrink-0"
                                src={evt.strHomeTeamBadge}
                              />
                            )}
                            <span
                              className={`text-sm truncate ${
                                isHome
                                  ? "text-white font-semibold"
                                  : "text-white/60"
                              }`}
                            >
                              {evt.strHomeTeam}
                            </span>
                            <span className="text-white font-bold text-sm mx-1">
                              {evt.intHomeScore} - {evt.intAwayScore}
                            </span>
                            {evt.strAwayTeamBadge && (
                              <img
                                alt=""
                                className="w-5 h-5 object-contain flex-shrink-0"
                                src={evt.strAwayTeamBadge}
                              />
                            )}
                            <span
                              className={`text-sm truncate ${
                                !isHome
                                  ? "text-white font-semibold"
                                  : "text-white/60"
                              }`}
                            >
                              {evt.strAwayTeam}
                            </span>
                          </div>
                          <span
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border flex-shrink-0 ${
                              won
                                ? "text-green-400 bg-green-500/15 border-green-500/30"
                                : draw
                                  ? "text-yellow-400 bg-yellow-500/15 border-yellow-500/30"
                                  : "text-red-400 bg-red-500/15 border-red-500/30"
                            }`}
                          >
                            {won ? "W" : draw ? "D" : "L"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <TeamDetailSquadTab
                decodedTeamName={decodedTeamName}
                loading={squadData.loading}
                positionGroups={squadData.positionGroups}
                sportsDbPlayers={squadData.sportsDbPlayers}
                teamColors={teamColors}
                onPlayerClick={handlePlayerClick}
              />
            </>
          )}

          {activeTab === "fixtures" && (
            <TeamDetailFixturesTab
              decodedTeamName={decodedTeamName}
              fixtures={{
                upcoming: fixturesData.upcoming,
                recent: fixturesData.recent,
              }}
              loading={fixturesData.loading}
              teamColors={teamColors}
              onGameClick={handleGameClick}
            />
          )}

          {activeTab === "standings" && (
            <TeamDetailStandingsTab
              decodedTeamName={decodedTeamName}
              leagueName={leagueName}
              loading={standingsData.loading}
              standings={standingsData.standings}
              teamColors={teamColors}
              teamRowRef={teamRowRef}
            />
          )}

          {activeTab === "about" && (
            <TeamDetailAboutTab
              aboutExpanded={aboutExpanded}
              apiSportsTeam={null}
              equipment={aboutData.equipment}
              honours={aboutData.honours}
              loading={aboutData.loading}
              navigate={navigate}
              recentEvents={recentEvents}
              setAboutExpanded={setAboutExpanded}
              sportsDbTeam={sportsDbTeam}
              teamColors={teamColors}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WebPlayerTeamDetail;
